/**
 * ResearchHeroInput - Hero section input for authenticated dashboard
 *
 * Features:
 * - Minimal, expandable textarea
 * - Keyboard shortcuts support (Ctrl+K, /)
 * - Optional file attachments (authenticated users)
 * - Research intensity selector (Quick/Standard/Comprehensive)
 */

import { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { ArrowRight, Loader2, Coins, X, Zap, Brain, Lock, Paperclip, FileText, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import { getBestAvailableIntensity, hasMinimumCredits, isIntensityAllowed } from './IntensitySelector';
import type { ResearchIntensity } from '@/types';
import { INTENSITY_CONFIGS, INTENSITY_CREDIT_THRESHOLDS } from '@/types';
import type { ResearchMode } from '@/types/researchModes';

export interface ResearchHeroInputHandle {
  focus: () => void;
  setQuery: (query: string) => void;
}

interface ResearchHeroInputProps {
  onSubmit: (query: string, intensity: ResearchIntensity, mode: ResearchMode, files?: File[]) => void;
  isSubmitting?: boolean;
  isFreeUser?: boolean;
  className?: string;
  /** User's current credit balance for intensity restrictions */
  creditBalance?: number | null;
  /** Whether user has unlimited credits */
  isUnlimited?: boolean;
  /** Whether user is authenticated */
  isAuthenticated?: boolean;
  /** Default intensity level */
  defaultIntensity?: ResearchIntensity;
  /** Callback when intensity changes - used to update credit estimates */
  onIntensityChange?: (intensity: ResearchIntensity) => void;
}

export const ResearchHeroInput = forwardRef<ResearchHeroInputHandle, ResearchHeroInputProps>(
  function ResearchHeroInput({
    onSubmit,
    isSubmitting = false,
    className,
    creditBalance,
    isUnlimited = false,
    isAuthenticated = false,
    defaultIntensity = 'fast',
    onIntensityChange,
  }, ref) {
    const [query, setQuery] = useState('');
    const [intensity, setIntensityState] = useState<ResearchIntensity>(defaultIntensity);
    const [showIntensityDropdown, setShowIntensityDropdown] = useState(false);

    // Wrapper to notify parent when intensity changes
    const setIntensity = useCallback((newIntensity: ResearchIntensity) => {
      setIntensityState(newIntensity);
      onIntensityChange?.(newIntensity);
    }, [onIntensityChange]);

    // Sync with defaultIntensity prop changes and notify parent of initial value
    useEffect(() => {
      setIntensityState(defaultIntensity);
      onIntensityChange?.(defaultIntensity);
    }, [defaultIntensity, onIntensityChange]);

    const [isFocused, setIsFocused] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const intensityDropdownRef = useRef<HTMLDivElement>(null);

    // Check if user has minimum credits to perform any research
    const canResearch = hasMinimumCredits(creditBalance, isUnlimited);

    // Auto-resize textarea to fit content
    const autoResize = useCallback(() => {
      const textarea = inputRef.current;
      if (!textarea) return;
      textarea.style.height = 'auto';

      const maxHeight = isExpanded ? 500 : 220;
      const minHeight = isExpanded ? 200 : 0;
      const targetHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
      textarea.style.height = `${targetHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [isExpanded]);

    // Expose focus and setQuery methods to parent
    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      setQuery: (q: string) => setQuery(q),
    }));

    // Auto-shift intensity based on credit balance
    // When credit balance changes, adjust intensity to the best available option
    useEffect(() => {
      if (!isAuthenticated) return; // Don't auto-shift for unauthenticated users

      const bestIntensity = getBestAvailableIntensity(creditBalance, isUnlimited, isAuthenticated);

      // Only downgrade if current selection is no longer affordable
      // Don't auto-upgrade to avoid surprising the user
      const currentThreshold = INTENSITY_CREDIT_THRESHOLDS[intensity];
      const balance = creditBalance ?? 0;

      if (!isUnlimited && balance < currentThreshold) {
        setIntensity(bestIntensity);
      }
    }, [creditBalance, isUnlimited, isAuthenticated, intensity]);

    // Auto-focus on mount
    useEffect(() => {
      const timer = setTimeout(() => inputRef.current?.focus(), 500);
      return () => clearTimeout(timer);
    }, []);

    // Resize on query change
    useEffect(() => {
      autoResize();
    }, [query, autoResize]);

    // Close dropdowns when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (intensityDropdownRef.current && !intensityDropdownRef.current.contains(event.target as Node)) {
          setShowIntensityDropdown(false);
        }
      };

      if (showIntensityDropdown) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [showIntensityDropdown]);

    const handleSubmit = () => {
      if (!query.trim() || isSubmitting) return;
      onSubmit(query.trim(), intensity, 'auto' as ResearchMode, pendingFiles.length > 0 ? pendingFiles : undefined);
      // Clear pending files after submit - they will be uploaded to the session by the parent
      setPendingFiles([]);
    };

    // Handle file selection for pre-research attachment
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      setFileError(null);
      const maxFiles = 5;
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.csv', '.xlsx', '.xls', '.json', '.xml'];
      const skippedReasons: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (pendingFiles.length + i >= maxFiles) {
          skippedReasons.push(`Max ${maxFiles} files allowed`);
          break;
        }
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!allowedExtensions.includes(ext)) {
          skippedReasons.push(`"${file.name}" - unsupported file type`);
          continue;
        }
        if (file.size > maxSize) {
          skippedReasons.push(`"${file.name}" - exceeds 10MB limit`);
          continue;
        }
        setPendingFiles(prev => {
          if (prev.length >= maxFiles) return prev;
          // Avoid duplicates by name
          if (prev.some(f => f.name === file.name)) return prev;
          return [...prev, file];
        });
      }

      // Show feedback for skipped files
      if (skippedReasons.length > 0) {
        setFileError(skippedReasons[0]);
        // Auto-clear error after 4 seconds
        setTimeout(() => setFileError(null), 4000);
      }

      // Reset input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleFileRemove = (fileName: string) => {
      setPendingFiles(prev => prev.filter(f => f.name !== fileName));
    };

    // Get current configs for display
    const currentIntensityConfig = INTENSITY_CONFIGS[intensity];

    // Intensity icons mapping
    const INTENSITY_ICONS: Record<ResearchIntensity, React.ReactNode> = {
      fast: <Zap className="w-3.5 h-3.5" />,
      deep: <Brain className="w-3.5 h-3.5" />,
    };

  return (
    <div className={cn("w-full max-w-2xl mx-auto space-y-3", className)}>
      {/* Insufficient Credits Warning */}
      {isAuthenticated && !canResearch && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <Coins className="w-4 h-4 text-warning flex-shrink-0" />
            <p className="text-sm text-text-secondary truncate">
              Need {INTENSITY_CREDIT_THRESHOLDS.fast}+ credits to research (balance: {creditBalance ?? 0}).
            </p>
          </div>
          <button
            onClick={() => window.location.href = '/plans'}
            className="shrink-0 rounded-lg border border-warning/40 bg-warning/10 px-3 py-1.5 text-sm text-warning hover:bg-warning/15 transition-colors"
          >
            Get credits
          </button>
        </div>
      )}

      <div className={cn("relative", !canResearch && isAuthenticated && "opacity-50 pointer-events-none")}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.json,.xml"
          onChange={handleFileSelect}
          className="hidden"
        />

        <div
          data-tour="research-input"
          className={cn(
            "relative rounded-2xl border bg-surface backdrop-blur-xl transition-all shadow-lg shadow-black/20",
            isFocused ? "border-primary/50 shadow-primary/10" : "border-border-subtle hover:border-border-light"
          )}
        >
          {/* File chips */}
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-4 pt-3">
              {pendingFiles.map((file) => (
                <div
                  key={file.name}
                  className="flex items-center gap-1.5 rounded-lg border border-border bg-surface-light/30 px-2.5 py-1 text-xs text-text-secondary"
                >
                  <FileText className="w-3 h-3 text-text-muted flex-shrink-0" />
                  <span className="max-w-[180px] truncate">{file.name}</span>
                  <button
                    onClick={() => handleFileRemove(file.name)}
                    className="text-text-muted hover:text-text-primary transition-colors flex-shrink-0"
                    title="Remove file"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* File error */}
          {fileError && (
            <div className="px-4 pt-2">
              <p className="text-[11px] text-error">{fileError}</p>
            </div>
          )}

          {/* Main input row */}
          <div className="flex items-end gap-3 px-4 py-3">
            <textarea
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (canResearch) handleSubmit();
                }
              }}
              placeholder={canResearch ? "Research any topic, company, or question..." : 'Purchase credits to continue…'}
              disabled={isSubmitting || !canResearch}
              rows={1}
              aria-label="Research query"
              className="flex-1 resize-none bg-transparent text-base text-text-primary placeholder:text-text-secondary/60 outline-none disabled:cursor-not-allowed leading-relaxed"
              style={{ overflow: 'hidden' }}
            />

            <button
              onClick={handleSubmit}
              disabled={!query.trim() || isSubmitting || !canResearch}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-full border transition-colors shrink-0',
                query.trim() && !isSubmitting && canResearch
                  ? 'border-primary/30 bg-primary text-black hover:bg-primary-hover'
                  : 'border-border bg-surface-light/30 text-text-muted cursor-not-allowed'
              )}
              aria-label="Run research"
              title="Run research"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Controls row — always visible */}
          <div className="flex items-center justify-between px-4 pb-3">
              <div className="flex items-center gap-1.5">
                {isAuthenticated && (
                  <Tooltip content="Attach PDFs, docs, or spreadsheets" position="top">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting || !canResearch}
                      className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors',
                        'border-border bg-surface-light/20 text-text-muted hover:text-text-primary hover:bg-surface-light/30',
                        (isSubmitting || !canResearch) && 'opacity-50 cursor-not-allowed'
                      )}
                      aria-label="Attach documents"
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                  </Tooltip>
                )}

                <div ref={intensityDropdownRef} data-tour="intensity-selector" className="relative">
                    <button
                      onClick={() => setShowIntensityDropdown(!showIntensityDropdown)}
                      disabled={isSubmitting}
                      className={cn(
                        'inline-flex h-8 items-center gap-1.5 px-2.5 rounded-lg border transition-colors text-xs',
                        'border-border bg-surface-light/20 text-text-muted hover:text-text-primary hover:bg-surface-light/30',
                        showIntensityDropdown && 'border-primary/30 text-primary',
                        isSubmitting && 'opacity-50 cursor-not-allowed'
                      )}
                      aria-label="Research depth"
                    >
                      {INTENSITY_ICONS[intensity]}
                      <span className="hidden xs:inline">{currentIntensityConfig.label}</span>
                    </button>

                  {showIntensityDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 w-44 overflow-hidden rounded-xl border border-border bg-surface shadow-xl z-50">
                      {(['fast', 'deep'] as ResearchIntensity[]).map((i) => {
                        const config = INTENSITY_CONFIGS[i];
                        const isSelected = i === intensity;
                        const allowed = isIntensityAllowed(i, creditBalance, isUnlimited, isAuthenticated);

                        return (
                          <button
                            key={i}
                            onClick={() => {
                              if (!allowed) return;
                              setIntensity(i);
                              setShowIntensityDropdown(false);
                            }}
                            disabled={!allowed}
                            className={cn(
                              'w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                              !allowed && 'opacity-50 cursor-not-allowed',
                              isSelected
                                ? 'bg-surface-light/40 text-text-primary'
                                : allowed
                                  ? 'text-text-secondary hover:bg-surface-light/30 hover:text-text-primary'
                                  : 'text-text-muted'
                            )}
                          >
                            <span className={cn(isSelected ? 'text-primary' : 'text-text-muted')}>{INTENSITY_ICONS[i]}</span>
                            <span className="flex-1 min-w-0 truncate">{config.label}</span>
                            {!allowed && <Lock className="w-3.5 h-3.5 text-text-muted" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Expand/collapse toggle for longer inputs */}
              <Tooltip content={isExpanded ? 'Collapse input' : 'Expand input'} position="top">
                <button
                  onClick={() => setIsExpanded(prev => !prev)}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors',
                    'border-border bg-surface-light/20 text-text-muted hover:text-text-primary hover:bg-surface-light/30',
                    isExpanded && 'border-primary/30 text-primary'
                  )}
                  aria-label={isExpanded ? 'Collapse input' : 'Expand input'}
                >
                  <ChevronsUpDown className="w-4 h-4" />
                </button>
              </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
});

